/**
 * User Slice
 * Manages user state and details
 */

import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { storefrontAPI } from '../../utils/api';
import { SupportedCurrency, UserDetailsFormData, ValidEmailResponse } from '../types';
import { authenticate, signIn } from './auth-slice';

// Extended state with loading (original UserState from types + isLoading)
export interface UserSliceState {
    balance: number;
    balanceCurrency: SupportedCurrency;
    email: string;
    firstName: string;
    lastName: string;
    validEmail: boolean;
    zipCode: string;
    phoneNumber: string;
    isLoading: boolean;
}

// Initial State
export const userInitialState: UserSliceState = {
    balance: 0,
    balanceCurrency: SupportedCurrency.USD,
    email: '',
    firstName: '',
    lastName: '',
    validEmail: false,
    zipCode: '',
    phoneNumber: '',
    isLoading: false,
};

// Async Thunks

/**
 * Check if user email is valid
 */
export const checkValidEmail = createAsyncThunk<ValidEmailResponse, void, { rejectValue: string }>(
    'user/checkValidEmail',
    async (_, { rejectWithValue }) => {
        try {
            const response = await storefrontAPI.checkValidEmail();
            return response;
        } catch {
            return rejectWithValue('Failed to check email validity');
        }
    },
);

/**
 * Update user email only
 */
export const updateUserEmail = createAsyncThunk<void, string, { rejectValue: string }>(
    'user/updateUserEmail',
    async (email, { rejectWithValue }) => {
        try {
            const response = await storefrontAPI.updateEmail(email);
            if (response.error) {
                return rejectWithValue(response.error);
            }
        } catch {
            return rejectWithValue('Failed to update email');
        }
    },
);

/**
 * Update all user details
 */
export const updateUserDetails = createAsyncThunk<void, UserDetailsFormData, { rejectValue: string }>(
    'user/updateUserDetails',
    async (formData, { rejectWithValue }) => {
        try {
            const response = await storefrontAPI.updateUserDetails(formData);
            if (response.error) {
                return rejectWithValue(response.error);
            }
        } catch {
            return rejectWithValue('Failed to update user details');
        }
    },
);

// Slice
export const userSlice = createSlice({
    name: 'user',
    initialState: userInitialState,
    reducers: {
        setUserBalance(state, action: PayloadAction<number>) {
            state.balance = action.payload;
        },
        updateBalance(state, action: PayloadAction<number>) {
            state.balance += action.payload;
        },
        setUserCurrency(state, action: PayloadAction<SupportedCurrency>) {
            state.balanceCurrency = action.payload;
        },
        setUserEmail(state, action: PayloadAction<string>) {
            state.email = action.payload;
        },
        setValidEmail(state, action: PayloadAction<boolean>) {
            state.validEmail = action.payload;
        },
        setUserDetails(state, action: PayloadAction<Partial<UserSliceState>>) {
            Object.assign(state, action.payload);
        },
        resetUser(state) {
            Object.assign(state, userInitialState);
        },
    },
    extraReducers: (builder) => {
        // Handle signIn - update user balance, currency, profile, and email validity
        builder.addCase(signIn.fulfilled, (state, action) => {
            state.balance = action.payload.balance;
            if (action.payload.partner_currency) {
                state.balanceCurrency = action.payload.partner_currency as SupportedCurrency;
            }
            if (action.payload.email) {
                state.email = action.payload.email;
            }
            if (action.payload.firstname) {
                state.firstName = action.payload.firstname;
            }
            if (action.payload.lastname) {
                state.lastName = action.payload.lastname;
            }
            if (action.payload.email_status) {
                state.validEmail = action.payload.email_status.valid_email;
            }
        });

        // Handle authenticate - update user balance
        builder.addCase(authenticate.fulfilled, (state, action) => {
            state.balance = action.payload.wallet_balance;
        });

        // Check Valid Email
        builder.addCase(checkValidEmail.fulfilled, (state, action) => {
            state.validEmail = action.payload.valid_email;
        });

        // Update User Details
        builder
            .addCase(updateUserDetails.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(updateUserDetails.fulfilled, (state) => {
                state.isLoading = false;
                state.validEmail = true;
            })
            .addCase(updateUserDetails.rejected, (state) => {
                state.isLoading = false;
            });

        // Update User Email
        builder
            .addCase(updateUserEmail.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(updateUserEmail.fulfilled, (state) => {
                state.isLoading = false;
                state.validEmail = true;
            })
            .addCase(updateUserEmail.rejected, (state) => {
                state.isLoading = false;
            });
    },
});

// Export actions
export const {
    setUserBalance,
    updateBalance,
    setUserCurrency,
    setUserEmail,
    setValidEmail,
    setUserDetails,
    resetUser,
} = userSlice.actions;

// Selectors
type RootState = { user: UserSliceState };

export const selectUser = (state: RootState): UserSliceState => state.user;
export const selectUserBalance = createSelector(selectUser, (user) => user.balance);
export const selectUserCurrency = createSelector(selectUser, (user) => user.balanceCurrency);
export const selectHasValidEmail = createSelector(selectUser, (user) => user.validEmail);
export const selectUserEmail = createSelector(selectUser, (user) => user.email);
export const selectUserLoading = createSelector(selectUser, (user) => user.isLoading);

export default userSlice.reducer;
